import { useState } from 'react';
import { useSelector } from 'react-redux';

import Button from '../../../components/buttons/Button';
import CurrencySelect from '../../../components/currencySelect/CurrencySelect';
import InputFormField from '../../../components/InputFormField/InputFormField';
import { ICurrencyFilterOption } from '../../../models/i-currency-filter-option.interface';
import { IState } from '../../../models/reducers/i-state.interface';
import { IWalletCurrency } from '../../../models/wallet-currency-response.interface';
import { parseToDecimalCurrency } from '../../../utils/parser.utils';
import { IPriceRangeFieldValue } from './models/i-price-range-field-value.interface';

import styles from './PriceRangeField.module.scss';

const EMPTY_PRICE_RANGE_FIELD_FILTERS = {
  currency: undefined,
  maxValue: undefined,
  minValue: undefined,
};

interface IPriceRangeFieldProps {
  onChange: (value: IPriceRangeFieldValue) => void;
}

const PriceRangeField = ({ onChange }: IPriceRangeFieldProps) => {
  const currencies = useSelector((state: IState): IWalletCurrency[] | null => state.wallet.currencies);
  const [currency, setCurrency] = useState<ICurrencyFilterOption | undefined>();
  const [minPrice, setMinPrice] = useState<string>('');
  const [maxPrice, setMaxPrice] = useState<string>('');

  const reset = () => {
    setMinPrice('');
    setMaxPrice('');
    setCurrency(undefined);
    onChange({ ...EMPTY_PRICE_RANGE_FIELD_FILTERS });

    return;
  };

  const updatePriceRange = () => {
    if (!currency) {
      reset();

      return;
    }

    const walletCurrency = currencies?.find((walletCurrency: IWalletCurrency) => walletCurrency.id === currency.value);

    if (!walletCurrency || (!maxPrice && !minPrice)) {
      onChange({ ...EMPTY_PRICE_RANGE_FIELD_FILTERS });

      return;
    }

    onChange({
      currency,
      maxValue: maxPrice ? +parseToDecimalCurrency(+maxPrice, walletCurrency) : undefined,
      minValue: minPrice ? +parseToDecimalCurrency(+minPrice, walletCurrency) : undefined,
    });
  };

  return (
    <div className={styles['filters-group']}>
      <CurrencySelect
        clearable={true}
        placeholder="currency"
        className="currency"
        value={currency}
        onChange={(option) => (!option ? reset() : setCurrency(option as ICurrencyFilterOption))}
      />
      <div className={styles['range']}>
        <InputFormField
          classNames={styles['input']}
          type="number"
          placeholder="MIN"
          value={minPrice}
          onChange={(event) => setMinPrice(event.target.value)}
        />
        <span>to</span>
        <InputFormField
          classNames={styles['input']}
          type="number"
          placeholder="MAX"
          value={maxPrice}
          onChange={(event) => setMaxPrice(event.target.value)}
        />
      </div>
      <Button className={styles['button']} size="small" text="Apply" type="button" onClick={updatePriceRange} />
    </div>
  );
};

export default PriceRangeField;
